import React, { FC, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ServicesCartContainer } from '@components/organisms/ServicesCartContainer/ServicesCartContainer';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Card } from '@components/atoms/Card';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { useData } from '@hooks/useData';
import { useServicesCart } from '@hooks/useServicesCart';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { SITE_URLS } from '@constants';
import { useGetAllPaymentMethods } from '@services/useGetAllPaymentMethods';
import {
    formatDateTime,
    formatDateToDayMonth,
    formatMultibancoReference,
    formatPhoneNumber,
    formatPrice,
    formatTime,
} from '@utils/format';
import styles from './ServicesBookingFeedback.module.css';

export const ServicesBookingFeedback: FC = () => {
    const location = useLocation();
    const { navigate } = useCustomNavigate();
    const { request: requestAllPaymentMethods } = useGetAllPaymentMethods();
    const { login, allPaymentMethods, createdReservations } = useData();
    const { displayWorkerStep, scheduleType } = useServicesCart();
    const { t, i18n } = useTranslation();

    const translations = {
        title: t('services-booking-feedback-component.title'),
        confirmedMessage: t('services-booking-feedback-component.confirmed-message'),
        thankYouMessage: t('services-booking-feedback-component.thank-you-message'),
        feedbackMessageSentToEmailNonPaid: t('services-booking-feedback-component.feedback-message-sent-to-email-non-paid'),
        mbEntityLabel: t('services-booking-feedback-component.multibanco.entity'),
        mbReferenceLabel: t('services-booking-feedback-component.multibanco.reference'),
        mbValueLabel: t('services-booking-feedback-component.multibanco.value'),
        priceLabel: t('services-booking-feedback-component.price.label'),
        feedbackMessage: t('services-booking-feedback-component.feedback-message', {
            returnObjects: true,
            name: `${login?.data?.user?.name} ${login?.data?.user?.lastName}`,
        }) as string[],
        feedbackMessageWaitingMbwayPayment: t('services-booking-feedback-component.feedback-message-waiting-mbway-payment', {
            phoneNumber: formatPhoneNumber(createdReservations?.data?.payment_phone_number || ''),
        }),
        feedbackMessageWaitingMultibancoPayment: t(
            'services-booking-feedback-component.feedback-message-waiting-multibanco-payment',
            {
                expiration: formatDateTime(createdReservations?.data?.payment_expiration || ''),
            }
        ),
        feedbackMessageWaitingCardPayment: t('services-booking-feedback-component.feedback-message-waiting-card-payment'),
        feedbackMessageSentToEmailPaid: t('services-booking-feedback-component.feedback-message-sent-to-email-paid'),
        returnToMarketplaceButton: t('services-booking-feedback-component.return-to-marketplace.button'),
    };

    useEffect(() => {
        translations.feedbackMessageWaitingMbwayPayment = t('feedback-component.feedback-message-waiting-mbway-payment', {
            phoneNumber: formatPhoneNumber(createdReservations?.data?.payment_phone_number || ''),
        });
        translations.feedbackMessageWaitingMultibancoPayment = t(
            'feedback-component.feedback-message-waiting-multibanco-payment',
            {
                expiration: formatDateTime(createdReservations?.data?.payment_expiration || ''),
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdReservations?.data]);

    const CURRENT_STEP = displayWorkerStep ? 4 : 3;

    const openedCardPaymentRef = useRef(false);

    // if user is not logged in, he can't access this page
    useEffect(() => {
        if (login === null) {
            const redirectTo = encodeURIComponent(location.pathname + location.search);
            navigate(`/${SITE_URLS.login}?redirectTo=${redirectTo}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    // call services, if we don't have the information yet
    useEffect(() => {
        !allPaymentMethods && requestAllPaymentMethods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allPaymentMethods, i18n.language]);

    // redirect for card payment
    useEffect(() => {
        if (createdReservations?.data?.payment_url && !openedCardPaymentRef.current) {
            openedCardPaymentRef.current = true;
            // Open the external link in a new tab automatically
            window.open(createdReservations?.data?.payment_url, '_blank', 'noopener,noreferrer');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdReservations?.data?.payment_url]);

    return (
        <ServicesCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <Anchor href={SITE_URLS.home} mode={AnchorMode.primary}>
                    {translations.returnToMarketplaceButton}
                </Anchor>
            }
            displayTotal={false}>
            <Card classes="p-4">
                <Container classes="text-center" customSize="custom-medium">
                    <>
                        <Typography color="success" fontWeight="900" fontSize="36" tagVariant={TypographyTagVariants.span}>
                            <i className="fa-solid fa-badge-check" />
                        </Typography>
                        {
                            /* reservation without payment */
                            scheduleType === 'preBook' ? (
                                <>
                                    <Typography classes="mt-4 mb-3 py-1" fontWeight="800" fontSize="24">
                                        {translations.confirmedMessage}
                                    </Typography>
                                    <div>
                                        {createdReservations?.data?.schedule_date && (
                                            <div className={styles['confirmed-datetime']}>
                                                <Typography fontSize="24">
                                                    <>
                                                        {formatDateToDayMonth(
                                                            new Date(createdReservations.data.schedule_date),
                                                            i18n.language
                                                        )}
                                                        <>
                                                            {createdReservations.data.schedule_date &&
                                                                `, ${formatTime(createdReservations.data.schedule_date + ' ' + createdReservations?.data?.schedule_time || '')}`}
                                                        </>
                                                    </>
                                                </Typography>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-3 mb-4 py-1">
                                        <Typography
                                            color="success"
                                            fontWeight="800"
                                            fontSize="20"
                                            breakpointsStyles={{ md: { fontSize: '24' } }}>
                                            {translations.thankYouMessage}
                                        </Typography>
                                    </div>

                                    <hr className="my-4 py-1" />

                                    <Typography fontSize="16">{translations.feedbackMessageSentToEmailNonPaid}</Typography>
                                </>
                            ) : (
                                /* paid reservation */ <>
                                    <div className="my-4 py-1">
                                        {[...translations.feedbackMessage].map((line, index) => (
                                            <Typography
                                                key={`line-${index}`}
                                                color="success"
                                                fontWeight="800"
                                                fontSize="20"
                                                breakpointsStyles={{ md: { fontSize: '24' } }}>
                                                {line}
                                            </Typography>
                                        ))}
                                    </div>
                                    <hr className="my-4 py-1" />
                                    {createdReservations?.data?.payment_method?.image?.src ? (
                                        <div className="my-4 py-1">
                                            <img
                                                src={createdReservations.data.payment_method.image.src}
                                                alt={createdReservations.data.payment_method.name}
                                            />
                                        </div>
                                    ) : (
                                        <></>
                                    )}

                                    {createdReservations?.data?.payment_method?.method === 'mbway' && (
                                        <>
                                            <div
                                                className={`${styles['payment-specific-feedback']} my-4`}
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(translations.feedbackMessageWaitingMbwayPayment),
                                                }}></div>
                                            <Typography classes="my-4 py-1" fontWeight="800" fontSize="16">
                                                <>
                                                    {`${translations.priceLabel}: `}
                                                    <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                                        {formatPrice(createdReservations?.data?.total)}
                                                    </Typography>
                                                </>
                                            </Typography>
                                        </>
                                    )}

                                    {createdReservations?.data?.payment_method?.method === 'multibanco' && (
                                        <>
                                            <div
                                                className={`${styles['payment-specific-feedback']} my-4`}
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(
                                                        translations.feedbackMessageWaitingMultibancoPayment
                                                    ),
                                                }}></div>
                                            <div className="my-4 text-start d-inline-block">
                                                <Typography classes="py-1" fontWeight="800" fontSize="16">
                                                    <>
                                                        {`${translations.mbEntityLabel}: `}
                                                        <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                                            {createdReservations?.data?.payment_entity}
                                                        </Typography>
                                                    </>
                                                </Typography>
                                                <Typography classes="py-1" fontWeight="800" fontSize="16">
                                                    <>
                                                        {`${translations.mbReferenceLabel}: `}
                                                        <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                                            {formatMultibancoReference(
                                                                createdReservations?.data?.payment_reference || ''
                                                            )}
                                                        </Typography>
                                                    </>
                                                </Typography>
                                                <Typography classes="py-1" fontWeight="800" fontSize="16">
                                                    <>
                                                        {`${translations.mbValueLabel}: `}
                                                        <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                                            {formatPrice(createdReservations?.data?.total)}
                                                        </Typography>
                                                    </>
                                                </Typography>
                                            </div>
                                        </>
                                    )}

                                    {createdReservations?.data?.payment_method?.method === 'card' && (
                                        <>
                                            <div
                                                className={`${styles['payment-specific-feedback']} my-4`}
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(translations.feedbackMessageWaitingCardPayment),
                                                }}></div>
                                            <Typography classes="my-4 py-1" fontWeight="800" fontSize="16">
                                                <>
                                                    {`${translations.priceLabel}: `}
                                                    <Typography tagVariant={TypographyTagVariants.span} color="primary">
                                                        {formatPrice(createdReservations?.data?.total)}
                                                    </Typography>
                                                </>
                                            </Typography>
                                        </>
                                    )}

                                    <hr className="my-4 py-1" />
                                    <Typography fontSize="16">{translations.feedbackMessageSentToEmailPaid}</Typography>
                                </>
                            )
                        }
                    </>
                </Container>
            </Card>
        </ServicesCartContainer>
    );
};
